import { useSortable } from '@dnd-kit/sortable';
import {
  Button,
  HorizontalDivider,
  Icon,
  Image,
  Modal,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Table } from '@hero/molecules';
import { getFormattedPrice } from '@hero/utils';

import * as S from './style';
function ImagesCell(props) {
  const value = props.getValue();
  return <Image src={value[0].url} width={36} height={36} />;
}

ImagesCell.propTypes = {
  getValue: PropTypes.func.isRequired
};

function RowDragHandleCell(props) {
  const { attributes, listeners } = useSortable({
    id: props.row.id
  });

  return (
    // Alternatively, you could set these attributes on the rows themselves
    <Button variant="link" color="neutral" {...attributes} {...listeners}>
      <Icon name="drag-handle" />
    </Button>
  );
}

RowDragHandleCell.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.string.isRequired
  }).isRequired
};

function RearrangeModal({ open, products, updateProducts, onClose }) {
  const { t } = useTranslation();
  const [rearrangedProducts, setRearrangedProducts] = useState([]);

  useEffect(() => {
    setRearrangedProducts(products.map((product, index) => ({ ...product, position: index + 1 })));
  }, [products]);

  const [globalFilters, setGlobalFilters] = useState({
    searchTerm: ''
  });

  const columns = React.useMemo(
    () => [
      {
        id: 'drag-handle',
        header: '',
        cell: RowDragHandleCell,
        size: 50
      },
      {
        header: t('position'),
        accessorKey: 'position',
        editable: true,
        type: 'number',
        componentProps: {
          min: 1,
          max: products?.length
        },
        accessorFn: row => {
          return row.position;
        },
        size: 100
      },
      {
        header: t('image'),
        accessorKey: 'images',
        cell: ImagesCell,
        size: 100
      },
      { header: t('code'), accessorKey: 'merchantProductId', size: 200 },
      { header: t('name'), accessorKey: 'name', size: 300 },
      {
        header: t('price'),
        accessorKey: 'prices',
        cell: ({ getValue }) => {
          const value = getValue();

          return getFormattedPrice({ min: value?.min, max: value?.max });
        }
      }
    ],
    [products]
  );

  function reorderProducts(products, product, order) {
    const newProducts = [...products];
    const currentIndex = newProducts.findIndex(
      p => p.merchantProductId === product.merchantProductId
    );

    if (currentIndex === -1) {
      throw new Error('not found');
    }

    const targetIndex = Math.max(0, Math.min(order - 1, newProducts.length - 1));
    const [removedProduct] = newProducts.splice(currentIndex, 1);
    newProducts.splice(targetIndex, 0, removedProduct);

    return newProducts.map((product, index) => ({
      ...product,
      position: index + 1
    }));
  }

  const updateData = (product, column, value) => {
    if (column === 'position') {
      const newProducts = reorderProducts(rearrangedProducts, product, value);
      setRearrangedProducts(newProducts.sort((a, b) => a.position - b.position));
    }
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      title={t('rearrangeItems')}
      size="xl"
      secondaryButtonProps={{
        children: t('cancel'),
        onClick: () => {
          setRearrangedProducts(products);
          onClose();
        }
      }}
      primaryButtonProps={{
        children: t('save'),
        onClick: () => {
          onClose();
          updateProducts(
            rearrangedProducts,
            <Trans i18nKey="rearrangeItemsSuccessMessage" components={{ b: <b /> }} />
          );
        }
      }}
    >
      <S.ModalContent>
        <Typography variant="body1Bold">{t('rearrangeItemsModalDescription')}</Typography>
        <HorizontalDivider $margin="16px 0" />
        <S.TableHeader>
          <Typography variant="body2Bold">{t('items')}</Typography>
          <VerticalDivider $height="20px" $margin="0 8px" />
          <Typography variant="body2">
            <Trans
              i18nKey="total"
              values={{
                count: products.length
              }}
              components={{
                b: <b />
              }}
            />
          </Typography>
        </S.TableHeader>
        <Table
          updateData={updateData}
          setRearrangeData={setNewArrangeData => {
            const newArrangeData = setNewArrangeData(rearrangedProducts);
            setRearrangedProducts(
              newArrangeData.map((product, index) => ({ ...product, position: index + 1 }))
            );
          }}
          data={rearrangedProducts.filter(product =>
            product?.name?.toLowerCase()?.includes(globalFilters.searchTerm.toLowerCase())
          )}
          getRowId={row => row.merchantProductId}
          pageCount={1}
          columns={columns}
          globalFilters={globalFilters}
          pagination={{
            pageIndex: 0,
            pageSize: 1,
            total: products?.length
          }}
          paginationProps={{
            enabled: false
          }}
          setPagination={() => {}}
          setGlobalFilters={setGlobalFilters}
          manualPagination
          enabled={{ filter: false, search: true, header: true }}
          emptyTableContent={{
            feature: 'collections',
            icon: 'collections'
          }}
        />
      </S.ModalContent>
    </Modal>
  );
}

RearrangeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  products: PropTypes.array.isRequired,
  updateProducts: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export { RearrangeModal };
